import { JSONPath } from 'jsonpath-plus';
import { Log } from '../../AuxillaryService/Log';
import * as DataList from '../../DataList';
import { List } from '../../DataList/List';
import { DataListColumn, ListData, RowData, RowWithNormalizedData } from '../../DataList/types';
import { getZoneById } from '../../Display';
import { pushReceiver } from '../../MessagingClient/instance';
import { TemplateEngine, TemplateWidgetMixin } from '../../Template';
import { isNullish } from '../../Utility/Nullish';
import { TimelineSlot, WidgetType } from '../types';
import { WidgetWithScheduler } from '../WidgetWithScheduler';
import {
    ListTransformMethod,
    ListWidgetConfiguration,
    ListWidgetConfigurationDetails,
    ListWidgetTemplateData,
    ZodListWidgetConfigurationDetails,
} from './types';

export class ListWidget extends TemplateWidgetMixin(WidgetWithScheduler) {
    readonly type: WidgetType.LIST = WidgetType.LIST;
    declare readonly config: ListWidgetConfigurationDetails;
    schedulerRunInterval: number;
    list: List | null;
    cellData: {
        rows: Array<RowData | RowWithNormalizedData | Record<string, unknown>> | null;
    };
    listData: ListData | null;
    totalPages: number;
    currentPage: number;
    pagingTimer?: NodeJS.Timer;
    lastUpdate?: number;

    constructor(widgetConfiguration: ListWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);

        /**
         * Number of seconds between each time the `Scheduler` runs
         */
        this.schedulerRunInterval = 14400; // 4 hours (failsafe interval for MercuryFx)

        /**
         * @type {List}
         */
        this.list = null;
        this.cellData = { rows: null };
        this.listData = null;
        this.totalPages = 1;
        this.currentPage = 0;

        /**
         * An interval function's id used with paging
         */
        this.pagingTimer = undefined;

        /**
         * Last timestamp that the widget was updated.
         */
        this.lastUpdate = undefined;
    }

    protected validateConfigurationDetails(
        maybeListWidgetConfigurationDetails: ListWidgetConfigurationDetails
    ): ListWidgetConfigurationDetails {
        return ZodListWidgetConfigurationDetails.parse(maybeListWidgetConfigurationDetails);
    }

    async initialize(): Promise<void> {
        await super.initialize();
        this._addUpdateListener(this.config.list_id);
        await this._subscribeToList(this.config.list_id);
    }

    /**
     * Queries the API for list data and updates the widget.
     */
    private async _updateListWidget(): Promise<void> {
        this.lastUpdate = Date.now();
        this.currentPage = 0;

        try {
            this.showPage(0);

            const dataList = await DataList.Factory.createList(this.config.list_id, 1, false, null);

            this.list = dataList;

            this.cellData = {
                //we now have both canonical and normalized data available to
                //filter on
                rows: [],
            };

            this.listData = new ListData(
                JSON.parse(
                    JSON.stringify({
                        list: dataList.config,
                    })
                )
            );

            this.setCellDataRows();

            this.hasContentToPlay =
                this.cellData.rows && this.cellData.rows.length === 0 ? false : true;

            const zoneObj = getZoneById(this.zoneId).returnZoneInfo();

            const widgetWidth =
                !isNullish(this.config.width) && this.config.width !== ''
                    ? parseInt(this.config.width)
                    : zoneObj.w;

            this.setColumnWidth(widgetWidth);

            await this.renderDataList();
            Log.Info.bind(null, `List Widget Rendered: ${this.id}`, null, __fileline);
        } catch (error) {
            Log.Error(
                `Failed to render List widget: ( ${this.id} : ${this.name})`,
                null,
                __fileline,
                error
            );
            throw error;
        }
    }

    setColumnWidth(widgetWidth: number): void {
        if (
            this.listData &&
            this.listData.list &&
            this.listData.list.columns &&
            this.listData instanceof ListData
        ) {
            const columnWidth = widgetWidth / this.listData.list.columns.length;

            this.listData.list.columns.forEach(function (col: DataListColumn) {
                col.width_in_pixels = columnWidth;
            });
        }
    }

    /**
     * @inheritDoc
     */
    schedulerAction(): void {
        // add 1 second so that it updates even when the last update was due to
        // scheduler
        if (
            !this.lastUpdate ||
            this.lastUpdate < Date.now() - this.schedulerRunInterval * 1000 + 1000
        ) {
            //no updates received from MercuryFx. This is not a worrisome condition
            //but we will re-query the API just in case something was missed.

            Log.Info(`data is stale for list ${this.config.list_id}`, null, __fileline, null);

            // ignored promise.
            this._updateListWidget();
        } else {
            Log.Info(`data is up-to-date for list ${this.config.list_id}`, null, __fileline, null);
        }
    }

    /**
     * Determine number of pages of the data list
     */
    determinePages(): void {
        try {
            const eventsHolder = document.getElementById(`eventsholder_${this.id}`);

            if (!eventsHolder) {
                throw new Error('Events holder element not found');
            }
            const header = eventsHolder.querySelector<HTMLElement>('.data-list-header');
            const labels = eventsHolder.querySelector<HTMLElement>('.data-list-labels');
            if (!header || !labels) {
                throw new Error('Header/label elements not found');
            }

            const listHeaderHeight = $(header).outerHeight();
            const listLabelsHeight = $(labels).outerHeight();

            if (listHeaderHeight === undefined || listLabelsHeight === undefined) {
                throw new Error('List header or label heights are undefined.');
            }

            let pageHeight = this.wrapperElement.offsetHeight - listHeaderHeight - listLabelsHeight;
            if (this.config.enable_pagination && this.config.display_page_indicator) {
                pageHeight -= ListWidget.getIndicatorHeight(eventsHolder);
            }

            const eventsHolderHeight =
                eventsHolder.offsetHeight - listHeaderHeight - listLabelsHeight;

            this.totalPages = Math.ceil(eventsHolderHeight / pageHeight);

            if (this.totalPages > 1 && this.config.enable_pagination) {
                //carve up pages
                const pageElements = [];

                for (let x = 0; x < this.totalPages; x++) {
                    pageElements.push(this.createPageElement(x));
                }

                //if exists, first remove
                const existingPage = this.wrapperElement.querySelector<HTMLElement>(
                    `.data-list-page-${this.id}`
                );

                if (existingPage) {
                    existingPage.remove();
                }

                pageElements.forEach((element) => {
                    eventsHolder.appendChild(element);
                });

                const listRows = this.wrapperElement.querySelectorAll<HTMLElement>(
                    '.data-list-items .data-list-row'
                );

                let pageNum = 0;
                let pageRowCount = 0;
                let domElem: HTMLElement | null = null;
                for (let x = 0; x < listRows.length; x++) {
                    domElem = document.getElementById(`data-list-${this.id}-page-${pageNum}`);

                    if (!domElem) {
                        //No page element exists, we need to add another page.
                        const pageElement = this.createPageElement(pageNum);
                        eventsHolder.appendChild(pageElement);

                        domElem = document.getElementById(`data-list-${this.id}-page-${pageNum}`);

                        if (!domElem) {
                            throw new Error('Invalid page element');
                        }

                        //increment totalPages
                        this.totalPages++;
                    }

                    pageRowCount++;
                    domElem.appendChild(listRows[x].cloneNode(true));

                    if (domElem.offsetHeight > pageHeight) {
                        //go to next page
                        const lastItem =
                            domElem.querySelector<HTMLElement>(`.data-list-row:last-child`);

                        if (this.config.display_page_indicator) {
                            domElem.appendChild(
                                ListWidget.getIndicatorElement(pageNum, this.totalPages, pageHeight)
                            );
                        }

                        //Only remove last item to add to the next page
                        //if there is more than 1 row on the page, otherwise it can
                        //result in an infinite loop that crashes the signage
                        if (pageRowCount > 1 && lastItem !== null) {
                            lastItem.remove();
                            x--;
                        }

                        pageNum++;
                        pageRowCount = 0;
                    }
                }

                if (domElem && this.config.display_page_indicator) {
                    domElem.appendChild(
                        ListWidget.getIndicatorElement(
                            this.totalPages - 1,
                            this.totalPages,
                            pageHeight
                        )
                    );
                }
                const items = document.querySelectorAll<HTMLElement>(
                    `#eventsholder_${this.id} .data-list-items`
                );
                //hide the items

                if (items.length > 0) {
                    items[0].style.display = 'none';
                }
            } else {
                this.totalPages = 1;
            }
        } catch (err) {
            Log.Warning(
                `Failed to get total number of pages for the List Widget: ${this.id}`,
                null,
                __fileline,
                null
            );
            throw err;
        }
    }

    /**
     * Creates a page element used when pagination is enabled
     */
    createPageElement(pageNumber: number): HTMLDivElement {
        const pageElement = document.createElement('div');
        pageElement.style.position = 'absolute';
        pageElement.style.width = '100%';
        pageElement.style.visibility = 'hidden';
        pageElement.style.left = '-999999px';
        pageElement.classList.add('data-list-page', 'data-list-items', `data-list-page-${this.id}`);
        pageElement.id = `data-list-${this.id}-page-${pageNumber}`;
        return pageElement;
    }

    /**
     * Builds and returns html for page indicators.
     */
    static getIndicatorElement(
        pageNumber: number,
        pageCount: number,
        pageHeight: number
    ): HTMLDivElement {
        const indicatorDiv = document.createElement('div');
        indicatorDiv.style.position = 'absolute';
        indicatorDiv.style.left = '50%';
        indicatorDiv.style.top = `${pageHeight}px`;
        indicatorDiv.style.transform = 'translate(-50%, 0)';

        const selectedIndicator = document.createElement('div');
        selectedIndicator.classList.add('selected_indicator');
        const selectedIndicatorImage = document.createElement('div');
        selectedIndicatorImage.classList.add('selected_indicator_image');
        const unselectedIndicator = document.createElement('div');
        unselectedIndicator.classList.add('unselected_indicator');
        const unselectedIndicatorImage = document.createElement('div');
        unselectedIndicatorImage.classList.add('unselected_indicator_image');

        /*
         * Using `cloneNode` to duplicate the elements preventing `appendChild`
         * from moving the original elements location. Moving the `createElement`
         * calls inside the loop would work as well.
         * {@link https://developer.mozilla.org/en-US/docs/Web/API/Node/appendChild}
         */
        for (let i = 0; i < pageCount; i++) {
            const indicator = document.createElement('div');
            indicator.style.display = 'inline-block';

            if (i === pageNumber) {
                indicator.appendChild(selectedIndicator.cloneNode());
                indicator.appendChild(selectedIndicatorImage.cloneNode());
            } else {
                indicator.appendChild(unselectedIndicator.cloneNode());
                indicator.appendChild(unselectedIndicatorImage.cloneNode());
            }
            indicatorDiv.appendChild(indicator);
        }

        return indicatorDiv;
    }

    /**
     * Get the largest height of the displayed indicators
     */
    static getIndicatorHeight(eventsHolder: Element): number {
        const indicators = [
            'selected_indicator',
            'unselected_indicator',
            'selected_indicator_image',
            'unselected_indicator_image',
        ];
        let height = 0;

        indicators.forEach((indicator) => {
            let tempHeight = 0;
            const inspector = document.createElement('div');
            inspector.classList.add(indicator);
            eventsHolder.appendChild(inspector);
            try {
                if (!inspector.style.display || inspector.style.display !== 'none') {
                    tempHeight = inspector.offsetHeight;
                }
            } finally {
                inspector.remove();
            }

            if (tempHeight > height) {
                height = tempHeight;
            }
        });

        return height;
    }

    /**
     * Hides a page element by pageNumber
     */
    hidePage(pageNumber: number): void {
        const pageElement = document.getElementById(`data-list-${this.id}-page-${pageNumber}`);

        if (pageElement) {
            pageElement.style.visibility = 'hidden';
            pageElement.style.left = '-999999px';
        }
    }

    /**
     * Shows a page element by pageNumber
     */
    showPage(pageNumber: number): void {
        const pageElement = document.getElementById(`data-list-${this.id}-page-${pageNumber}`);

        if (pageElement) {
            pageElement.style.visibility = 'visible';
            pageElement.style.left = '0px';
        }
    }

    /**
     * Shows the next page element
     */
    nextPage(): void {
        //Hide current page
        this.hidePage(this.currentPage);

        //Set next page
        this.currentPage++;
        if (this.currentPage >= this.totalPages) {
            this.currentPage = 0;
        }

        //Show next page
        this.showPage(this.currentPage);
    }

    /**
     * Creates an interval to call nextPage() used for pagination
     */
    setPagingTimer(): void {
        this.clearPagingTimer();
        const interval = (this.duration * 1000) / this.totalPages;

        this.pagingTimer = setInterval(this.nextPage.bind(this), interval);
    }

    /**
     * Clears the interval this.pagingTimer used for pagination
     */
    clearPagingTimer(): void {
        if (this.pagingTimer) {
            clearInterval(this.pagingTimer);
            this.pagingTimer = undefined;
        }
    }

    /**
     * @inheritDoc
     */
    querySlots(numSlots: number, maxDuration: number): Array<TimelineSlot> {
        if (!this.totalPages) {
            return []; //defaulting to this as error
        }
        return super.querySlots(numSlots, maxDuration);
    }

    /**
     * @inheritDoc
     */
    async beforeFadeIn(): Promise<void> {
        if (this.list !== null && (this.config.transform_method || this.config.filter_expression)) {
            this.setCellDataRows();
            this.hasContentToPlay =
                this.cellData.rows && this.cellData.rows.length === 0 ? false : true;
            await this.renderDataList();
        }

        if (this.config.display_all_pages_before_duration_end === 'true') {
            this.hidePage(this.currentPage);
            this.currentPage = 0;
        }
        this.showPage(this.currentPage);
    }

    /**
     * @inheritDoc
     */
    async afterFadeIn(): Promise<void> {
        if (this.config.display_all_pages_before_duration_end === 'true') {
            this.setPagingTimer();
        }
    }

    /**
     * @inheritDoc
     */
    async beforeFadeOut(
        currentSlot: TimelineSlot,
        nextSlot: TimelineSlot
    ): Promise<{ cancelFade: boolean }> {
        //Clear the paging timer function
        this.clearPagingTimer();

        //cancel fade in/out if same widget and page count less than 2
        const cancelFade =
            nextSlot && currentSlot.widget.id === nextSlot.widget.id && this.totalPages < 2;

        return {
            cancelFade: cancelFade,
        };
    }

    /**
     * @inheritDoc
     */
    async afterFadeOut(currentSlot: TimelineSlot, nextSlot: TimelineSlot): Promise<void> {
        this.nextPage();
        return super.afterFadeOut(currentSlot, nextSlot);
    }

    /**
     * @listens MessagingClient#list-update
     */
    _addUpdateListener(listId: string): void {
        pushReceiver().addListener('list-update', (event) => {
            if (event.detail.listId === listId) {
                Log.Info(
                    `Received push notification from MessagingClient for list ${this.config.list_id}`,
                    null,
                    __fileline
                );

                this._updateListWidget();
            }
        });
    }

    /**
     * Determines if a function is a transform method
     * @param func
     */
    private isListTransformMethod(func: unknown): func is ListTransformMethod {
        return func instanceof Function && func.length == 1;
    }

    /**
     * Runs the widget's transform method on rowData.
     * @private
     */
    private runTransformMethod(
        rowData: Array<RowData | RowWithNormalizedData | Record<string, unknown>>
    ): Array<RowData | RowWithNormalizedData | Record<string, unknown>> {
        let transformMethod: unknown | undefined = undefined;
        try {
            transformMethod = new Function(
                'rows',
                `
                var userFunc = ${this.config.transform_method};
                return userFunc(rows);
            `
            );
        } catch (error) {
            Log.Warning(
                `Failed to create user defined transform method for the List Widget: ${this.id}`,
                null,
                __fileline,
                error
            );
        }

        try {
            rowData = this.isListTransformMethod(transformMethod)
                ? transformMethod(rowData)
                : rowData;
        } catch (error) {
            Log.Warning(
                `Failed to run the transform method for the List Widget: ${this.id}`,
                null,
                __fileline,
                error
            );
        }

        return rowData;
    }

    /**
     * Runs the widget's JSONPath filter expression on rowData.
     * @private
     */
    private runFilterExpression(
        rowData: Array<RowData | RowWithNormalizedData | Record<string, unknown>>
    ): Array<RowData | RowWithNormalizedData | Record<string, unknown>> {
        rowData = rowData.filter(
            (row: RowData | RowWithNormalizedData | Record<string, unknown>) => {
                // The function returns an empty array
                // if the evaluated expression is false
                const data = JSONPath({
                    path: this.config.filter_expression,
                    json: row,
                });

                return data.length > 0;
            }
        );

        return rowData;
    }

    /**
     * Sets this.cellData.rows
     * @private
     */
    private setCellDataRows(): void {
        const withNormalizedData =
            this.config.filter_expression || this.config.transform_method ? true : false;

        let allRowData = this.list ? this.list.serializeForSigns(withNormalizedData) : [];
        if (this.config.filter_expression && allRowData instanceof Array) {
            allRowData = this.runFilterExpression(allRowData);
        }

        if (this.config.transform_method) {
            allRowData = this.runTransformMethod(allRowData);
        }

        this.cellData.rows = allRowData;
    }

    private async _subscribeToList(listId: string): Promise<void> {
        try {
            await pushReceiver().subscribeToList(listId);

            Log.Info(
                `List Widget Subscribed. widgetsid=${this.id}, list_id=${listId}`,
                null,
                __fileline
            );
        } catch (error) {
            Log.Error(`Failed to subscribe to list channel ${listId}`, null, __fileline, error);
        }
    }

    /**
     * @inheritdoc
     * Updates list data and renders the widget
     */
    getReadyForDisplay(): Promise<void> {
        return this._updateListWidget();
    }

    /**
     * @inheritdoc
     * Clears the timer for pagination on stop
     */
    async stop(): Promise<void> {
        this.clearPagingTimer();
        return super.stop();
    }

    /**
     * Acquire template and render stories
     * Returns a Promise that resolves when the template has rendered
     */
    private async renderDataList(): Promise<void> {
        await this.renderContent();
        if (this.templateConfiguration?.framework === TemplateEngine.HANDLEBARS) {
            await this.determinePages();
        }
    }

    protected getRenderContext(): ListWidgetTemplateData {
        if (isNullish(this.listData) || isNullish(this.listData.list.columns)) {
            throw new Error('missing list data.');
        }

        const result = {
            list: this.cellData,
            cols: this.listData,
            wobj: {
                widget: {
                    widgetsid: this.id,
                    config: {
                        css: this.config.css,
                    },
                },
            },
            Widget: {
                Id: this.id,
            },
            Zone: {
                Id: this.zoneId,
            },
            viewBag: {
                widgetid: this.id,
            },
        };

        if (this.templateConfiguration?.framework === TemplateEngine.HANDLEBARS) {
            return result;
        } else {
            return {
                ...result,
                context: {
                    list: {
                        id: this.listData?.list.id,
                        name: this.listData?.list.data_list_name,
                        type: this.listData?.list.data_list_type,
                    },
                    columns: this.listData.list.columns,
                    rows: this.cellData.rows,
                    zone: {
                        id: this.zoneId,
                    },
                    widget: {
                        id: this.id,
                        config: {
                            css: this.config.css,
                            enablePagination: this.config.enable_pagination,
                        },
                    },
                },
            };
        }
    }
}
